import { 
  Box,
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  TextField, 
  Typography 
} from "@mui/material";
import { SublocalityDto } from "../../../api-client";
import { PhotoshopPicker } from "react-color";
import { useEffect, useMemo, useState } from "react";
import notificationStore from "../../../stores/Notification/notificationStore";
import { useCreateSublocality, useUpdateSublocality } from "../../../stores/Address/address.mutations";
import { useLocationId } from "../../../stores/User";

interface ModalSublocalityProps {
  editDialogOpen: boolean;
  editRow: SublocalityDto | null;
  sublocalities: SublocalityDto[];
  onClose: () => void;
}
const ModalSublocality: React.FC<ModalSublocalityProps>= ({editDialogOpen, editRow, sublocalities, onClose}) => {
  const [formData, setFormData] = useState<Partial<SublocalityDto>>({
      id: '',
      name: '',
      abbreviation: '',
      color: '',
    });
  const [showColorPicker, setShowColorPicker] = useState(false);
  
  // React Query hooks
  const createSublocality = useCreateSublocality();
  const updateSublocality = useUpdateSublocality();
  const locationId = useLocationId();
  
  // Initialize the editMode variable based on the existence of editRow
  const isEditMode = Boolean(editRow);

  // Extract unique colors from sublocalities prop
  const existingColors = useMemo(() => {
    if (!sublocalities || sublocalities.length === 0) return [];
    
    return sublocalities
    .map((sublocality) => sublocality.color)
    .filter(Boolean) // Remove null/undefined colors
    .filter((color, index, arr) => arr.indexOf(color) === index); // Remove duplicates
  }, [sublocalities]);
  // console.log("Existing Colors:", existingColors);

  // Initialize form data when editRow changes (initialize form fields with existing data)
  useEffect(() => {
    if (isEditMode && editRow) {
      setFormData({
        id: editRow.id || "",
        name: editRow.name || "",
        abbreviation: editRow.abbreviation || "",
        color: editRow.color || "",
      });
    } else {
      setFormData({
        id: "",
        name: "",
        abbreviation: "",
        color: "",
      });
    }
  }, [editRow, isEditMode] );

  const handleSaveEdit = async () => {
    try {
      if (isEditMode && editRow) {
        const updatedSublocality: SublocalityDto = {
          ...editRow,
          id: formData.id!,
          name: formData.name!,
          abbreviation: formData.abbreviation,
          color: formData.color
        };
        // Use React Query mutation for update
        await updateSublocality.mutateAsync({ id: editRow.id!, sublocalityDto: updatedSublocality });
        onClose();
        setShowColorPicker(false);
      } else {
        // CREATE CASE 
        // Check for duplicate ID
        const duplicateId = sublocalities.find(sub => sub.id === formData.id);
        if (duplicateId) {
          notificationStore.showMessage(`Ein Stadtteil mit der ID "${formData.id}" existiert bereits.`, 'error');
          return;
        }
        
        const newSublocality: SublocalityDto = {
          id: formData.id!,
          name: formData.name!,
          abbreviation: formData.abbreviation!,
          color: formData.color!,
          locationId
        };
        
        // Use React Query mutation for create
        await createSublocality.mutateAsync(newSublocality);
        onClose();
        setShowColorPicker(false);  
      }
    } catch (error) {
      console.error('Error saving sublocality:', error);
    }
  };
  
  return (
  <Dialog open={editDialogOpen} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        bgcolor: '#e3f2fd',
        color: 'rgba(0, 0, 0, 0.87)',
        display: 'flex', 
        alignItems: 'center',
        gap: 1
        }}>
          {isEditMode && editRow
          ? `Stadtteil bearbeiten: ${editRow.name}`
          : "Neuen Stadtteil erstellen"}
      </DialogTitle>

      <DialogContent sx={{ p: 3}}>
        {!isEditMode && (
          <TextField
            fullWidth
            label="ID"
            variant="outlined"
            margin="normal"
            name="id"
            value={formData.id}
            onChange={(e) => setFormData({ ...formData, id: e.target.value })}
          />
        )}
        
        {/* Name Field */}
        <TextField
          fullWidth
          label="Name"
          variant="outlined"
          margin="normal"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        
        {/* Abbreviation Field */}
        <TextField
          fullWidth
          label="Abkürzung"
          variant="outlined"
          margin="normal"
          name="abbreviation"
          value={formData.abbreviation}
          onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value }) }
        />
            
        {/* Color Selection */}
        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
          Farbe: 
        </Typography>
        
        {/* Color controls container */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {/* Color preview box */}
          <Box 
            sx={{ width: '40px', height: '40px', backgroundColor: formData.color, border: '2px solid #ddd', borderRadius: '8px',cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.15)'} }}
            onClick={() => setShowColorPicker(!showColorPicker)}
            title="Farbwähler öffnen"
          />

          {/* Color input field */}
          <TextField 
            label="Farbe Hex-Code"
            variant="outlined"
            name="color"
            placeholder='#ffffff'
            value={formData.color}
            onChange={(e) => setFormData({ ...formData, color: e.target.value }) }
            sx={{ width: '150px' }}
          />
        </Box>
        
        {/* Color Palette */}
        {existingColors.length > 0 && (
          <>
            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              Vorhandene Farben:
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {existingColors.map((color, index) => (
                <Box
                  key={index}
                  sx={{ 
                    width: '32px', 
                    height: '32px', 
                    backgroundColor: color, 
                    border: formData.color === color ? '3px solid #1976d2' : '1px solid #ccc',
                    borderRadius: '6px',
                    cursor: 'pointer', 
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                    transition: 'all 0.2s',
                    '&:hover': { 
                      transform: 'scale(1.1)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.2)' 
                    } 
                  }}
                  onClick={() => {
                    setFormData({ ...formData, color });
                    setShowColorPicker(false);
                  }}
                  title={color}
                />
              ))}
            </Box>
          </>
        )}

        {/* ChromePicker - Conditional Render*/}
        {showColorPicker && (
          <Box sx={{ 
            mt: 1.5,
            border: '1px solid #ccc',
            borderRadius: '8px',
            display: 'flex',
            justifyContent: 'center' }}>
            <PhotoshopPicker
              color={formData.color}
              onChange={(color:any) => setFormData({ ...formData, color: color.hex }) }
            />
          </Box>
        )}
      </DialogContent>
      
      {/* Dialog Actions */}
      <DialogActions sx={{ p: 1.5 }}>
        <Button
          onClick={() => {
            onClose();
            setShowColorPicker(false);
        }}
        >
          Abbrechen
        </Button>

        <Button onClick={handleSaveEdit}>
          Speichern
        </Button>

      </DialogActions>

    </Dialog>
  );
}
export default ModalSublocality;